import { useMemo } from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { ColorLabelToken, SingleSelect } from '@opallabs/lapidary'
import type { FieldMenuOption } from '../../../types'
import type { FieldInputProps, SingleSelectField } from '../../types'
import { useSwappableField } from '../../useSwappableField'
import { TextDisplay } from '../TextDisplay'

const mapOptions = (
  option: FieldMenuOption,
  colorsEnabled: boolean
): FieldMenuOption => ({
  ...option,
  color: colorsEnabled ? option.color : null,
})

export const FieldSingleSelect = ({
  colorsEnabled = false,
  editing,
  field,
  onChange,
  readOnly,
  selectOptions = {},
  setIsEditing,
  valueLeadingNode,
}: FieldInputProps<SingleSelectField>) => {
  const { setDisplayRef, onStopEditing } = useSwappableField({ setIsEditing })

  const activeOptions = useMemo(
    () => field.options.map((option) => mapOptions(option, colorsEnabled)),
    [colorsEnabled, field.options]
  )

  const [selectedValue, selectedColor, isUnavailable] = useMemo(() => {
    if (!field.data?.value) return [undefined, undefined, false]
    const isSelected = (option: FieldMenuOption) =>
      option.value === field.data?.value?.id
    const activeOption = field.options.find(isSelected)
    const selectedFieldValue =
      activeOption ?? field.deactivatedOptions.find(isSelected)
    if (!selectedFieldValue) return [undefined, undefined, false]

    return [
      mapOptions(selectedFieldValue, colorsEnabled),
      selectedFieldValue.color ?? undefined,
      !activeOption,
    ]
  }, [colorsEnabled, field.data, field.deactivatedOptions, field.options])

  const onSelect = (option: MenuOption | null) => {
    const { allowDeselection = true } = selectOptions
    if (!option && !allowDeselection) {
      return setIsEditing(false)
    }

    onChange({
      fieldId: field.id,
      data: {
        id: field.data?.id || null,
        value: option ? { id: option.value } : null,
      },
    })
  }

  if (!editing || readOnly) {
    return (
      <TextDisplay
        ref={setDisplayRef}
        fieldId={field.id}
        fieldLabel={field.displayName}
        leadingNode={valueLeadingNode}
        text={selectedValue?.label}
        readOnly={readOnly}
        setIsEditing={setIsEditing}
        color={selectedColor ?? field.color}
        colorsEnabled={colorsEnabled && Boolean(selectedColor)}
        isUnavailable={isUnavailable}
      />
    )
  }

  return (
    <SingleSelect.ControlledRoot
      options={activeOptions}
      onChange={onSelect}
      selectedOption={selectedValue ?? null}
    >
      <SingleSelect.SearchableBody
        initialIsOpen={true}
        isClearable={true}
        onBlur={onStopEditing}
        onOpenChange={(open) => (open ? setIsEditing(true) : onStopEditing())}
      >
        <SingleSelect.Trigger
          htmlId={`field-${field.id}`}
          hideLeadingNode={true}
        >
          {colorsEnabled && selectedValue && selectedColor && (
            <ColorLabelToken
              color={selectedColor}
              label={selectedValue.label}
            />
          )}
        </SingleSelect.Trigger>
        <SingleSelect.Menu>
          <SingleSelect.SearchBar />
          <SingleSelect.MenuItems />
        </SingleSelect.Menu>
      </SingleSelect.SearchableBody>
    </SingleSelect.ControlledRoot>
  )
}
